import { useCallback, useMemo } from "react";
import { UseDropdownProps, useDropdown } from "./use-dropdown";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { useI18n } from "./i18n-hooks";
import { useSnackbar } from "./snackbar";
import { useIsScreenSizeMin } from "./useIsScreenSizeMin";
import { DropdownItemsType } from "../contexts/DropdownManager";
import { useTheme } from "./use-theme";
import { GestureResponderEvent } from "react-native";

export type UseActionSheetOrDropdownProps = UseDropdownProps & {
    setIsLoading?: (isLoading: boolean) => void;
    destructiveButtonIndex?: number;
};

export const useActionSheetOrDropdown = ({
    items,
    setIsLoading,
    destructiveButtonIndex,
    ...props
}: UseActionSheetOrDropdownProps) => {
    const { showActionSheetWithOptions } = useActionSheet();
    const { showSnackbar } = useSnackbar();
    const isLarge = useIsScreenSizeMin("large");
    const i18n = useI18n();
    const { colors } = useTheme();

    const itemsWithCancel = useMemo<DropdownItemsType[]>(() => {
        const _ret = [
            ...items.map((i, index) => ({
                ...i,
                ...(index === destructiveButtonIndex
                    ? {
                          color: colors.error,
                      }
                    : {}),
                ...(isLarge
                    ? {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          onClick: async (e) => {
                              setIsLoading?.(true);
                              await i.onClick(e);
                              setIsLoading?.(false);
                          },
                      }
                    : {}),
            })),
        ];
        if (!isLarge) {
            _ret.push({
                label: i18n.t("cancel"),
                onClick: () => {},
            });
        }
        return _ret;
    }, [colors.error, destructiveButtonIndex, i18n, isLarge, items, setIsLoading]);

    const dropdownProps = useDropdown({
        items: itemsWithCancel,
        ...props,
    });

    const onSelectOption = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (selectedIndex = 0, data?: any) => {
            try {
                await itemsWithCancel[selectedIndex].onClick(data);
            } catch (e) {
                console.warn(e);
                showSnackbar({
                    text: i18n.t("documents.select_error"),
                    mode: "error",
                });
            }

            setIsLoading?.(false);
        },
        [itemsWithCancel, i18n, setIsLoading, showSnackbar],
    );

    const openActionSheet = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (_e?: GestureResponderEvent, data?: any) => {
            setIsLoading?.(true);
            try {
                showActionSheetWithOptions(
                    {
                        options: itemsWithCancel.map((i) => i.label),
                        cancelButtonIndex: items.length,
                        destructiveButtonIndex,
                    },
                    async (selectedIndex = 0) => {
                        await onSelectOption(selectedIndex, data);
                    },
                );
            } finally {
                /* empty */
            }
        },
        [
            setIsLoading,
            showActionSheetWithOptions,
            itemsWithCancel,
            items.length,
            destructiveButtonIndex,
            onSelectOption,
        ],
    );

    return { ref: dropdownProps.ref, onPress: isLarge ? dropdownProps.onPress : openActionSheet };
};
